import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import {
  MdSchedule,
  MdDelete,
  MdPerson,
  MdPeople,
  MdFlag,
  MdVisibility,
  MdEdit,
  MdAccessTime,
  MdToday,
  MdListAlt,
  MdAutorenew,
  MdClose,
  MdSearch,
} from "react-icons/md";
import ActionMenu from "../components/common/ActionMenu";
import { format } from "date-fns";

const API_URL =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_API_URL || window.location.origin
    : "http://localhost:6000";

const COLORS = [
  "#6366f1",
  "#f59e0b",
  "#10b981",
  "#ef4444",
  "#3b82f6",
  "#8b5cf6",
];

const getInitials = (name) =>
  name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";

// ── Multi-user dropdown with checkboxes (used when recurring is enabled) ─────
const MultiUserPicker = ({ users, value = [], onChange }) => {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const strIds = (value || []).map((v) => String(v));
  const selectedSet = new Set(strIds);
  const filtered = users.filter((u) =>
    u.name.toLowerCase().includes(search.toLowerCase()),
  );
  const selectedUsers = users.filter((u) => selectedSet.has(String(u._id)));
  const toggle = (id, checked) => {
    const sid = String(id);
    if (checked) {
      if (selectedSet.has(sid)) return;
      onChange([...strIds, sid]);
      return;
    }
    onChange(strIds.filter((v) => v !== sid));
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {/* Selected chips (quick visual summary) */}
      {selectedUsers.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
          {selectedUsers.map((u) => (
            <span
              key={u._id}
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                background: "var(--primary-light, #ede9fe)",
                color: "var(--primary)", borderRadius: 20,
                padding: "3px 10px 3px 8px", fontSize: "0.82rem", fontWeight: 600,
                border: "1px solid var(--primary)",
              }}
            >
              {u.name}
              <button
                type="button"
                onClick={() => toggle(u._id, false)}
                style={{ background: "none", border: "none", cursor: "pointer", padding: 0, lineHeight: 1, color: "var(--primary)" }}
                aria-label={`Remove ${u.name}`}
              >
                <MdClose size={13} />
              </button>
            </span>
          ))}
        </div>
      )}
      {/* Search input */}
      <div style={{ position: "relative" }}>
        <MdSearch size={15} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)", pointerEvents: "none" }} />
        <input
          className="form-control"
          style={{ paddingLeft: 30 }}
          placeholder="Search staff and tick checkboxes…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
        />
      </div>
      {/* Dropdown list with checkbox rows */}
      {open && (
        <div style={{
          position: "absolute", zIndex: 200, width: "100%", maxHeight: 220, overflowY: "auto",
          background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8,
          boxShadow: "0 4px 16px rgba(0,0,0,.12)", marginTop: 4,
        }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            padding: "8px 12px",
            borderBottom: "1px solid var(--border)",
            fontSize: "0.78rem",
            color: "var(--text-muted)",
          }}>
            <span>{selectedUsers.length} selected</span>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onChange([])}
              style={{
                border: "none",
                background: "transparent",
                color: "var(--primary)",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Clear all
            </button>
          </div>
          {filtered.length === 0 && (
            <div style={{ padding: "10px 14px", color: "var(--text-muted)", fontSize: "0.85rem" }}>
              No matching staff
            </div>
          )}
          {filtered.map((u) => (
            <label
              key={u._id}
              style={{
                padding: "8px 12px", cursor: "pointer", fontSize: "0.88rem",
                display: "flex", alignItems: "center", gap: 8,
              }}
              className="multi-picker-option"
            >
              <input
                type="checkbox"
                checked={selectedSet.has(String(u._id))}
                onChange={(e) => toggle(u._id, e.target.checked)}
                onClick={(e) => e.stopPropagation()}
              />
              <span style={{
                width: 28, height: 28, borderRadius: "50%", background: "var(--primary)",
                color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 700, flexShrink: 0,
              }}>
                {getInitials(u.name)}
              </span>
              <span>{u.name}</span>
              {u.designation && <span style={{ color: "var(--text-muted)", fontSize: "0.78rem" }}>· {u.designation}</span>}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

// Helper: normalise assignedTo from populated data (array of objects or IDs)
const normaliseAssignedTo = (raw, isRecurring) => {
  if (!raw) return isRecurring ? [] : "";
  if (Array.isArray(raw)) {
    const ids = raw.map((u) => (typeof u === "object" ? u._id : u)).filter(Boolean);
    if (isRecurring) return ids;
    return ids[0] || "";
  }
  const id = typeof raw === "object" ? raw._id : raw;
  return isRecurring ? (id ? [id] : []) : (id || "");
};

const ScheduledTasks = ({ mode = "create", initialSchedule }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [scheduled, setScheduled] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(initialSchedule?._id || null);

  const initIsRecurring = !!initialSchedule?.isRecurring;
  const [form, setForm] = useState(
    initialSchedule
      ? {
          title: initialSchedule.title || "",
          description: initialSchedule.description || "",
          assignedTo: normaliseAssignedTo(initialSchedule.assignedTo, initIsRecurring),
          priority: initialSchedule.priority || "medium",
          scheduleDate: initialSchedule.scheduleDate
            ? new Date(initialSchedule.scheduleDate).toISOString().slice(0, 16)
            : "",
          deadline: initialSchedule.deadline
            ? new Date(initialSchedule.deadline).toISOString().slice(0, 10)
            : "",
          tags: Array.isArray(initialSchedule.tags)
            ? initialSchedule.tags.join(", ")
            : "",
          isRecurring: initIsRecurring,
        }
      : {
          title: "",
          description: "",
          assignedTo: "",      // string for non-recurring, array for recurring
          priority: "medium",
          scheduleDate: "",
          deadline: "",
          tags: "",
          isRecurring: false,
        },
  );

  const load = async () => {
    try {
      setLoading(true);
      const [schedRes, usersRes] = await Promise.all([
        api.get("/scheduled-tasks"),
        api.get("/users/list"),
      ]);
      setScheduled(schedRes.data.data || []);
      setUsers(usersRes.data.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleChange = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleToggle = (key) => (e) => {
    if (key === "isRecurring") {
      const nowRecurring = e.target.checked;
      setForm((f) => {
        // Convert assignedTo between string (single) and array (multi) formats
        let at = f.assignedTo;
        if (nowRecurring) {
          at = at ? [at] : [];           // string → array
        } else {
          at = Array.isArray(at) ? (at[0] || "") : at;  // array → first item
        }
        return { ...f, isRecurring: nowRecurring, assignedTo: at };
      });
    } else {
      setForm((f) => ({ ...f, [key]: e.target.checked }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const assignedToIsEmpty = form.isRecurring
      ? !Array.isArray(form.assignedTo) || form.assignedTo.length === 0
      : !form.assignedTo;
    if (!form.title || assignedToIsEmpty || !form.scheduleDate) {
      toast.error("Title, assignee and schedule date are required");
      return;
    }
    const scheduleISO = new Date(form.scheduleDate).toISOString();
    if (Number.isNaN(new Date(scheduleISO).getTime())) {
      toast.error("Please choose a valid schedule date and time");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        // Always send an array; backend normalises it
        assignedTo: form.isRecurring
          ? (Array.isArray(form.assignedTo) ? form.assignedTo : [form.assignedTo])
          : [form.assignedTo],
        priority: form.priority,
        scheduleDate: scheduleISO,
        deadline: form.deadline ? new Date(form.deadline).toISOString() : null,
        tags: form.tags,
        isRecurring: !!form.isRecurring,
      };
      if (editingId) {
        await api.put(`/scheduled-tasks/${editingId}`, payload);
        toast.success("Schedule updated");
      } else {
        await api.post("/scheduled-tasks", payload);
        toast.success("Schedule created");
      }
      setForm({ title: "", description: "", assignedTo: "", priority: "medium", scheduleDate: "", deadline: "", tags: "", isRecurring: false });
      setEditingId(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save schedule");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Cancel this scheduled task?")) return;
    try {
      await api.delete(`/scheduled-tasks/${id}`);
      toast.success("Scheduled task cancelled");
      await load();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to delete scheduled task",
      );
    }
  };

  const handleAssignNow = async (st) => {
    if (!st?._id) return;
    setAssigningScheduleId(st._id);
    try {
      const { data } = await api.post(`/scheduled-tasks/${st._id}/assign-now`);
      toast.success(data?.message || "Scheduled task assigned");
      await load();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to assign scheduled task",
      );
    } finally {
      setAssigningScheduleId(null);
    }
  };
  const toggleRowSelected = (id, checked) => {
    setSelectedScheduleIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return [...next];
    });
  };

  const handleAssignSelected = async () => {
    if (selectedScheduleIds.length === 0) return;
    setBulkAssigning(true);
    try {
      const { data } = await api.post("/scheduled-tasks/assign-now/bulk", {
        scheduleIds: selectedScheduleIds,
      });
      const assignedCount = data?.data?.assigned?.length || 0;
      const failedCount = data?.data?.failed?.length || 0;
      toast.success(
        `Assigned ${assignedCount} schedule(s)${
          failedCount ? `, ${failedCount} failed` : ""
        }`,
      );
      setSelectedScheduleIds([]);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Bulk assign failed");
    } finally {
      setBulkAssigning(false);
    }
  };

  const now = new Date();

  // Single avatar + name (used for Scheduled By column)
  const renderUserCell = (u) => {
    if (!u) return "—";
    const imgSrc = u.avatar
      ? u.avatar.startsWith("http")
        ? u.avatar
        : `${API_URL}${u.avatar}`
      : null;
    const bg = COLORS[(u.name?.charCodeAt(0) || 0) % COLORS.length];
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div
          className="avatar"
          style={{
            width: 28,
            height: 28,
            fontSize: 11,
            background: imgSrc ? "transparent" : bg,
          }}
        >
          {imgSrc ? (
            <img
              src={imgSrc}
              alt={u.name}
              onError={(e) => {
                e.target.style.display = "none";
                e.target.parentNode.textContent = getInitials(u.name);
              }}
            />
          ) : (
            getInitials(u.name)
          )}
        </div>
        <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>{u.name}</span>
      </div>
    );
  };

  // Compact stacked-avatar cell for the Assigned To column (supports 1..N users)
  const MAX_VISIBLE = 3;
  const renderAssigneeCell = (raw) => {
    const list = Array.isArray(raw) ? raw.filter(Boolean) : raw ? [raw] : [];
    if (list.length === 0) return <span style={{ color: "var(--text-muted)" }}>—</span>;

    const visible = list.slice(0, MAX_VISIBLE);
    const overflow = list.length - MAX_VISIBLE;
    const allNames = list.map((u) => u?.name || "?").join(", ");

    if (list.length === 1) {
      // Single user: show avatar + name like renderUserCell
      const u = list[0];
      const imgSrc = u?.avatar
        ? u.avatar.startsWith("http") ? u.avatar : `${API_URL}${u.avatar}`
        : null;
      const bg = COLORS[(u?.name?.charCodeAt(0) || 0) % COLORS.length];
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div className="avatar" style={{ width: 28, height: 28, fontSize: 11, background: imgSrc ? "transparent" : bg }}>
            {imgSrc ? (
              <img src={imgSrc} alt={u.name} onError={(e) => { e.target.style.display = "none"; e.target.parentNode.textContent = getInitials(u.name); }} />
            ) : getInitials(u?.name)}
          </div>
          <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>{u?.name}</span>
        </div>
      );
    }

    // Multiple users: overlapping avatars + count chip
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6 }} title={allNames}>
        {/* Overlapping avatar stack */}
        <div style={{ display: "flex", alignItems: "center" }}>
          {visible.map((u, i) => {
            const imgSrc = u?.avatar
              ? u.avatar.startsWith("http") ? u.avatar : `${API_URL}${u.avatar}`
              : null;
            const bg = COLORS[(u?.name?.charCodeAt(0) || 0) % COLORS.length];
            return (
              <div
                key={u?._id || i}
                title={u?.name}
                className="avatar"
                style={{
                  width: 26, height: 26, fontSize: 10,
                  background: imgSrc ? "transparent" : bg,
                  marginLeft: i === 0 ? 0 : -8,
                  border: "2px solid var(--surface, #fff)",
                  zIndex: MAX_VISIBLE - i,
                  position: "relative",
                  flexShrink: 0,
                }}
              >
                {imgSrc ? (
                  <img src={imgSrc} alt={u?.name} onError={(e) => { e.target.style.display = "none"; e.target.parentNode.textContent = getInitials(u?.name); }} />
                ) : getInitials(u?.name)}
              </div>
            );
          })}
          {overflow > 0 && (
            <div style={{
              width: 26, height: 26, borderRadius: "50%", fontSize: 10, fontWeight: 700,
              background: "var(--surface2)", color: "var(--text-muted)",
              border: "2px solid var(--surface, #fff)",
              display: "flex", alignItems: "center", justifyContent: "center",
              marginLeft: -8, zIndex: 0, position: "relative", flexShrink: 0,
            }}>
              +{overflow}
            </div>
          )}
        </div>
        {/* Name summary */}
        <span style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--text)", maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {list.length <= 2
            ? list.map((u) => u?.name?.split(" ")[0]).join(", ")
            : `${list[0]?.name?.split(" ")[0]} +${list.length - 1} more`
          }
        </span>
      </div>
    );
  };

  const handleEdit = (st) => {
    const rec = !!st.isRecurring;
    setEditingId(st._id);
    setForm({
      title: st.title || "",
      description: st.description || "",
      assignedTo: normaliseAssignedTo(st.assignedTo, rec),
      priority: st.priority || "medium",
      scheduleDate: st.scheduleDate ? new Date(st.scheduleDate).toISOString().slice(0, 16) : "",
      deadline: st.deadline ? new Date(st.deadline).toISOString().slice(0, 10) : "",
      tags: Array.isArray(st.tags) ? st.tags.join(", ") : "",
      isRecurring: rec,
    });
  };

  const handleView = (st) => {
    navigate(`/scheduled-tasks/${st._id}`);
  };

  const showForm = mode === "create";
  // On the dedicated create page we ONLY show the form; list is for the /all route.
  const showList = mode === "list";

  // client-side search/filter + pagination for upcoming list
  const [search, setSearch] = useState("");
  const [fPriority, setFPriority] = useState("");
  const [fUser, setFUser] = useState("");
  const [assigningScheduleId, setAssigningScheduleId] = useState(null);
  const [selectedScheduleIds, setSelectedScheduleIds] = useState([]);
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);

  // Upcoming list:
  // - one-time schedules disappear after execution
  // - recurring schedules stay forever (they repeat monthly)
  const upcomingScheduled = scheduled.filter((st) => st.isRecurring || !st.executed);

  const filteredUpcoming = upcomingScheduled
    .filter((st) => {
      if (fPriority && st.priority !== fPriority) return false;
      if (fUser) {
        const ids = Array.isArray(st.assignedTo)
          ? st.assignedTo.map((u) => u?._id || u)
          : [st.assignedTo?._id || st.assignedTo];
        if (!ids.includes(fUser)) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        if (
          !st.title.toLowerCase().includes(q) &&
          !(Array.isArray(st.assignedTo)
            ? st.assignedTo.some((u) => u?.name?.toLowerCase().includes(q))
            : st.assignedTo?.name?.toLowerCase().includes(q)) &&
          !st.createdBy?.name?.toLowerCase().includes(q)
        ) {
          return false;
        }
      }
      return true;
    })
    .sort(
      (a, b) =>
        new Date(a.nextRunAt || a.scheduleDate) -
        new Date(b.nextRunAt || b.scheduleDate),
    );

  // Widgets should reflect what the table is showing (after filters)
  const oneHourFromNow = new Date(now.getTime() + 60 * 60 * 1000);
  const twentyFourHoursFromNow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const endOfToday = new Date(now);
  endOfToday.setHours(23, 59, 59, 999);

  const nearAssign1h = filteredUpcoming.filter((st) => {
    const d = new Date(st.nextRunAt || st.scheduleDate);
    return d >= now && d <= oneHourFromNow;
  }).length;
  const nearAssign24h = filteredUpcoming.filter((st) => {
    const d = new Date(st.nextRunAt || st.scheduleDate);
    return d >= now && d <= twentyFourHoursFromNow;
  }).length;
  const assignToday = filteredUpcoming.filter((st) => {
    const d = new Date(st.nextRunAt || st.scheduleDate);
    return d >= startOfToday && d <= endOfToday;
  }).length;

  const totalPages = Math.max(
    1,
    Math.ceil(filteredUpcoming.length / PAGE_SIZE),
  );
  const pageStartIndex = (page - 1) * PAGE_SIZE;
  const currentPageItems = filteredUpcoming.slice(
    pageStartIndex,
    pageStartIndex + PAGE_SIZE,
  );

  useEffect(() => {
    setPage(1);
  }, [search, fPriority, fUser]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return (
    <main className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Scheduled Tasks</h1>
          <p className="page-subtitle">
            Real tasks are created when the Assign On time is reached (server checks about every
            minute). Keep the API server running; disable jobs only with ENABLE_SCHEDULER=false.
          </p>
        </div>
      </div>

      {showForm && (
        <div className="card card-body" style={{ marginBottom: 24 }}>
          <h3
            style={{
              fontWeight: 800,
              marginBottom: 20,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <MdSchedule color="var(--primary)" />
            Schedule a task
          </h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Title *</label>
              <input
                className="form-control"
                value={form.title}
                onChange={handleChange("title")}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                rows={3}
                value={form.description}
                onChange={handleChange("description")}
              />
            </div>
            <div className="form-group">
              <label className="form-label" style={{ display: "flex", alignItems: "center", gap: 5 }}>
                {form.isRecurring
                  ? <><MdPeople size={14} /> Assign To (multiple allowed) *</>
                  : <><MdPerson size={14} /> Assign To *</>
                }
              </label>
              {form.isRecurring ? (
                <MultiUserPicker
                  users={users}
                  value={Array.isArray(form.assignedTo) ? form.assignedTo : (form.assignedTo ? [form.assignedTo] : [])}
                  onChange={(ids) => setForm((f) => ({ ...f, assignedTo: ids }))}
                />
              ) : (
                <select
                  className="form-control"
                  value={form.assignedTo}
                  onChange={handleChange("assignedTo")}
                  required={!form.isRecurring}
                >
                  <option value="">Select staff</option>
                  {users.map((u) => (
                    <option key={u._id} value={u._id}>{u.name}</option>
                  ))}
                </select>
              )}
            </div>
            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label">
                  <MdSchedule size={14} style={{ marginRight: 4 }} />
                  Assign On (date &amp; time) *
                </label>
                <input
                  className="form-control"
                  type="datetime-local"
                  value={form.scheduleDate}
                  onChange={handleChange("scheduleDate")}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  <MdFlag size={14} style={{ marginRight: 4 }} />
                  Priority
                </label>
                <select
                  className="form-control"
                  value={form.priority}
                  onChange={handleChange("priority")}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>
            <div className="form-group" style={{ marginTop: 4 }}>
              <label
                className="form-label"
                style={{ display: "flex", alignItems: "center", gap: 10 }}
              >
                <input
                  type="checkbox"
                  checked={!!form.isRecurring}
                  onChange={handleToggle("isRecurring")}
                  style={{ width: 16, height: 16 }}
                />
                <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <MdAutorenew size={18} color="var(--primary)" />
                  Enable recurring (monthly)
                </span>
              </label>
              <div
                style={{
                  marginTop: 6,
                  fontSize: "0.82rem",
                  color: "var(--text-muted)",
                }}
              >
                If enabled, this schedule will run every month on the same date and time.
              </div>
            </div>
            <div className="grid grid-2">
              <div className="form-group">
                <label className="form-label">Task Deadline (optional)</label>
                <input
                  className="form-control"
                  type="date"
                  value={form.deadline}
                  onChange={handleChange("deadline")}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Tags (comma-separated)</label>
                <input
                  className="form-control"
                  value={form.tags}
                  onChange={handleChange("tags")}
                  placeholder="design, bug, follow-up"
                />
              </div>
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving}
              style={{ marginTop: 16 }}
            >
              {saving ? "Scheduling..." : "Schedule Task"}
            </button>
          </form>
        </div>
      )}

      {showList && (
        <div
          className="card card-body"
          style={{ marginTop: showForm ? 24 : 0 }}
        >
          {/* Widgets row */}
          <div className="grid grid-4" style={{ marginBottom: 16, gap: 12 }}>
            <div className="card stat-card" style={{ boxShadow: "none" }}>
              <div className="stat-icon">
                <MdListAlt color="#7c3aed" size={22} />
              </div>
              <div className="stat-value">{filteredUpcoming.length}</div>
              <div className="stat-label">Total schedules</div>
            </div>
            <div className="card stat-card" style={{ boxShadow: "none" }}>
              <div className="stat-icon">
                <MdSchedule color="#38bdf8" size={22} />
              </div>
              <div className="stat-value">{upcomingScheduled.length}</div>
              <div className="stat-label">Waiting for Assign On</div>
            </div>
            <div className="card stat-card" style={{ boxShadow: "none" }}>
              <div className="stat-icon">
                <MdToday color="#4ade80" size={22} />
              </div>
              <div className="stat-value">{assignToday}</div>
              <div className="stat-label">Assigning today</div>
            </div>
            <div className="card stat-card" style={{ boxShadow: "none" }}>
              <div className="stat-icon">
                <MdAccessTime color="#fb923c" size={22} />
              </div>
              <div className="stat-value">
                {nearAssign1h} / {nearAssign24h}
              </div>
              <div className="stat-label">Near assign (1h / 24h)</div>
            </div>
          </div>

          {/* Filters / search */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <input
              className="form-control"
              style={{ maxWidth: 260 }}
              placeholder="Search title, staff, creator…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select
              className="form-control"
              style={{ width: "auto" }}
              value={fPriority}
              onChange={(e) => setFPriority(e.target.value)}
            >
              <option value="">All priorities</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
            <select
              className="form-control"
              style={{ width: "auto" }}
              value={fUser}
              onChange={(e) => setFUser(e.target.value)}
            >
              <option value="">All assignees</option>
              {users.map((u) => (
                <option key={u._id} value={u._id}>
                  {u.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-primary"
              disabled={bulkAssigning || selectedScheduleIds.length === 0}
              onClick={handleAssignSelected}
              title="Assign selected recurring tasks now"
            >
              {bulkAssigning
                ? "Assigning selected..."
                : `Assign selected (${selectedScheduleIds.length})`}
            </button>
            <span
              style={{
                marginLeft: "auto",
                fontSize: "0.8rem",
                color: "var(--text-muted)",
              }}
            >
              Showing {filteredUpcoming.length} upcoming
            </span>
          </div>

          <h3
            style={{
              fontWeight: 800,
              marginBottom: 12,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            Upcoming scheduled tasks
          </h3>
          {loading ? (
            <div className="loading">
              <div className="spinner" />
              <span className="loading-text">Loading...</span>
            </div>
          ) : filteredUpcoming.length === 0 ? (
            <div className="empty-state">
              <MdSchedule size={40} />
              <h3>No scheduled tasks</h3>
              <p>Adjust your filters or create a new schedule.</p>
            </div>
          ) : (
            <>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "center", width: 42 }}>Sel</th>
                      <th>Title</th>
                      <th>Scheduled By</th>
                      <th>Assigned To</th>
                      <th style={{ textAlign: "center", width: 70 }}>
                        Recurring
                      </th>
                      <th>Assign On</th>
                      <th>Deadline</th>
                      <th>Priority</th>
                      <th style={{ textAlign: "center", width: 120 }}>
                        Assign
                      </th>
                      <th style={{ textAlign: "center", width: 52 }}>•••</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentPageItems.map((st) => {
                      const runAt = st.nextRunAt || st.scheduleDate;
                      const isSelected = selectedScheduleIds.includes(st._id);
                      return (
                        <tr key={st._id}>
                          <td style={{ textAlign: "center" }}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) =>
                                toggleRowSelected(st._id, e.target.checked)
                              }
                              title="Select for bulk assign"
                            />
                          </td>
                          <td>{st.title}</td>
                          <td>{renderUserCell(st.createdBy)}</td>
                          <td>{renderAssigneeCell(st.assignedTo)}</td>
                          <td style={{ textAlign: "center" }}>
                            {st.isRecurring ? (
                              <span
                                title="Recurring monthly"
                                style={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: 10,
                                  background: "var(--surface2)",
                                  border: "1px solid var(--border)",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "var(--primary)",
                                }}
                              >
                                <MdAutorenew size={17} />
                              </span>
                            ) : (
                              <span style={{ color: "var(--text-muted)" }}>—</span>
                            )}
                          </td>
                          <td>
                            {format(new Date(runAt), "MMM d, yyyy")}
                            <div
                              style={{
                                fontSize: "0.7rem",
                                color: "var(--text-muted)",
                              }}
                            >
                              {format(new Date(runAt), "hh:mm a")}
                            </div>
                          </td>
                          <td>
                            {st.deadline
                              ? format(new Date(st.deadline), "MMM d, yyyy")
                              : "—"}
                          </td>
                          <td>
                            <span className={`badge badge-${st.priority}`}>
                              {st.priority}
                            </span>
                          </td>
                          <td style={{ textAlign: "center" }}>
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              onClick={() => handleAssignNow(st)}
                              disabled={assigningScheduleId === st._id}
                            >
                              {assigningScheduleId === st._id
                                ? "Assigning..."
                                : "Assign now"}
                            </button>
                          </td>
                          <td style={{ textAlign: "center" }}>
                            <ActionMenu
                              items={[
                                {
                                  label: "View schedule task",
                                  icon: <MdVisibility size={15} />,
                                  onClick: () => handleView(st),
                                },
                                {
                                  label: "Edit",
                                  icon: <MdEdit size={15} />,
                                  onClick: () =>
                                    navigate(`/scheduled-tasks/${st._id}/edit`),
                                  disabled: !!st.executed,
                                },
                                "divider",
                                {
                                  label: "Delete",
                                  icon: <MdDelete size={15} />,
                                  onClick: () => handleDelete(st._id),
                                  danger: true,
                                },
                              ]}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {filteredUpcoming.length > PAGE_SIZE && (
                <div
                  style={{
                    marginTop: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    fontSize: "0.8rem",
                  }}
                >
                  <span style={{ color: "var(--text-muted)" }}>
                    Showing{" "}
                    <strong>
                      {pageStartIndex + 1}-
                      {Math.min(
                        pageStartIndex + PAGE_SIZE,
                        filteredUpcoming.length,
                      )}
                    </strong>{" "}
                    of <strong>{filteredUpcoming.length}</strong> upcoming
                    schedules
                  </span>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <button
                      className="btn btn-sm btn-secondary"
                      type="button"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Prev
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (p) => (
                        <button
                          key={p}
                          type="button"
                          className="btn btn-sm"
                          onClick={() => setPage(p)}
                          style={{
                            minWidth: 30,
                            paddingInline: 8,
                            fontWeight: 600,
                            background:
                              p === page ? "var(--primary)" : "var(--surface2)",
                            color: p === page ? "#fff" : "var(--text)",
                            borderColor:
                              p === page ? "var(--primary)" : "var(--border)",
                          }}
                        >
                          {p}
                        </button>
                      ),
                    )}
                    <button
                      className="btn btn-sm btn-secondary"
                      type="button"
                      disabled={page >= totalPages}
                      onClick={() =>
                        setPage((p) => (p < totalPages ? p + 1 : totalPages))
                      }
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </main>
  );
};

export default ScheduledTasks;
